import { createRouter, createWebHistory } from "vue-router";
import { routesPublic } from "@/router/routes/RoutesPublic";
import { routesPrivate } from "@/router/routes/RoutesPrivate";
import { getCurrentUser } from "@/api/user";
import { useUserStore } from "@/store/user";
import { PermissionEnum } from "@/app/core/enums/PermissionEnum";
import { defaultLandingPath } from "@/utils/defaultLanding";
import { useSnackbar } from "@/composables/useSnackbar";

const routes = [
  {
    path: "",
    redirect: () => defaultLandingPath(),
  },
  ...routesPublic,
  ...routesPrivate,
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Collapse duplicate slashes (e.g. "//" or "///")
router.beforeEach((to, from, next) => {
  if (/\/{2,}/.test(to.path)) {
    return next({
      path: to.path.replace(/\/{2,}/g, "/"),
      query: to.query,
      hash: to.hash,
      replace: true,
    });
  }
  next();
});

// Mirrors the store's tokenCan getter: holding `admin` satisfies every check,
// matching User::tokenCan() on the backend.
const granted = (permissions, required) =>
  permissions.includes(PermissionEnum.Admin) ||
  required.some((permission) => permissions.includes(permission));

// Walks the whole matched chain so a child's meta.permissions narrows its
// parent's rather than replacing it. A route without its own meta.permissions
// inherits whatever its ancestors require.
const requiredFor = (to) =>
  to.matched
    .map((record) => record.meta?.permissions)
    .filter((permissions) => Array.isArray(permissions) && permissions.length);

router.beforeEach(async (to, from, next) => {
  if (!to.meta.requireAuth) {
    return next();
  }

  const toLogin = () =>
    next({ path: "/login", query: { redirect: to.fullPath } });

  let permissions;
  try {
    const user = useUserStore();
    permissions = user.user.permissions;
    if (!user.token) {
      const response = await getCurrentUser();
      permissions = response.permissions;
    }
  } catch (error) {
    console.error("User not authenticated", error);
    return toLogin();
  }

  if (!Array.isArray(permissions)) {
    return toLogin();
  }

  if (!requiredFor(to).every((required) => granted(permissions, required))) {
    useSnackbar().error("You don't have access to that page.");
    return from.matched.length ? next(false) : next(defaultLandingPath());
  }

  next();
});

export default router;
